import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { Store } from '@ngrx/store';
import { filter, Observable, Subject, takeUntil, tap } from 'rxjs';

import { ModalsActions } from 'src/app/features/modals';
import { PackageUser } from 'src/app/features/package-users';
import { IntervalTimer } from 'src/app/features/shared/util/interval-timer';
import { RootStoreState } from 'src/app/store';

import { MultiFactorChallengeOptions, MultiFactorCodeValidationStates } from '../../models';
import { MultiFactorActions } from '../../store/actions';
import { MultiFactorSelectors } from '../../store/selectors';

@Component({
    selector: 'app-multi-factor-challenge-modal',
    templateUrl: './multi-factor-challenge-modal.component.html',
    styleUrls: ['./multi-factor-challenge-modal.component.scss'],
    standalone: false
})
export class MultiFactorChallengeModalComponent implements OnInit, OnDestroy {
  @Input() packageUser: PackageUser;

  attemptsRemaining$: Observable<number>;
  challengeCode = new UntypedFormControl('', [Validators.required]);
  challengeCodeErrorMessage: string;
  challengeCompleteLoading$: Observable<boolean>;
  codeValidationStates = MultiFactorCodeValidationStates;
  currentlySelectedChallengeType$: Observable<MultiFactorChallengeOptions>;
  errorMessage$: Observable<string>;
  intervalTimer: IntervalTimer = new IntervalTimer(30);
  multiFactorChallengeOptions = MultiFactorChallengeOptions;
  multiFactorInProgress$: Observable<boolean>;
  onDestroyNotifier = new Subject();
  userCodeValidationState: MultiFactorCodeValidationStates = MultiFactorCodeValidationStates.hide;

  constructor(private readonly store: Store<RootStoreState.State>) {
    this.handleAttemptsRemaining = this.handleAttemptsRemaining.bind(this);
    this.setUserCodeValidationState = this.setUserCodeValidationState.bind(this);

    this.attemptsRemaining$ = this.store.select(MultiFactorSelectors.getAttemptsRemaining);
    this.challengeCompleteLoading$ = this.store.select(MultiFactorSelectors.getCompleteMultiFactorChallengeLoading);
    this.currentlySelectedChallengeType$ = this.store.select(MultiFactorSelectors.getCurrentlySelectedChallengeType);
    this.errorMessage$ = this.store.select(MultiFactorSelectors.getErrorMessage);
    this.multiFactorInProgress$ = this.store.select(MultiFactorSelectors.isChallengeInProgress);
  }

  ngOnInit(): void {
    this.attemptsRemaining$
      .pipe(
        takeUntil(this.onDestroyNotifier),
        filter((attempts) => !!attempts),
        tap(this.handleAttemptsRemaining)
      )
      .subscribe();

      const defaultOption = this.packageUser?.phoneNumber
        ? MultiFactorChallengeOptions.SMS
        : MultiFactorChallengeOptions.EMAIL;
      this.updateSelectedChallengeOption(defaultOption);
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier.next(undefined);
    this.onDestroyNotifier.complete();
  }

  cancelMultiFactorChallenge() {
    this.store.dispatch(MultiFactorActions.CancelMultiFactorChallenge());
  }

  setUserCodeValidationState(v: MultiFactorCodeValidationStates) {
    this.userCodeValidationState = v;
  }

  createMultiFactorChallenge() {
    this.store.dispatch(ModalsActions.ShowLoadingSpinner());
    this.store.dispatch(
      MultiFactorActions.CreateMultiFactorChallenge({
        payload: {
          packageUserGuid: this.packageUser?.packageUserGuid,
        },
      })
    );

    this.intervalTimer.start(() =>
      this.setUserCodeValidationState(MultiFactorCodeValidationStates.hide)
    );
  }

  completeMultiFactorChallenge() {
    this.store.dispatch(
      MultiFactorActions.CompleteMultiFactorChallenge({
        payload: { challengeCode: this.challengeCode.value },
      })
    );
  }

  resendMultiFactorChallenge() {
    this.handleValidationMessage();
    if (this.intervalTimer.getRemainingSeconds() <= 0) {
      this.store.dispatch(MultiFactorActions.ResendMultiFactorChallengeCode());

      this.intervalTimer.start(() =>
        this.setUserCodeValidationState(MultiFactorCodeValidationStates.hide)
      );
    }
  }

  handleAttemptsRemaining(attempts) {
    this.challengeCodeErrorMessage = `Invalid Security Code. ${attempts} attempts remaining`;
    this.challengeCode.setErrors({ invalid: this.challengeCodeErrorMessage });
    this.challengeCode.setValue('');
  }

  handleValidationMessage() {
    if (this.intervalTimer.getRemainingSeconds() >= 1) {
      this.setUserCodeValidationState(MultiFactorCodeValidationStates.invalid);
    } else {
      this.setUserCodeValidationState(MultiFactorCodeValidationStates.valid);
    }
  }

  change(e: MatRadioChange) {
    this.updateSelectedChallengeOption(e.value);
  }

  updateSelectedChallengeOption(challengeOption: MultiFactorChallengeOptions) {
    this.store.dispatch(
      MultiFactorActions.MultiFactorChallengeSelected({
        payload: { selectedChallengeType: challengeOption },
      })
    );
  }
}